import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  Outlet,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { RequireAuth } from "@/lib/auth/guard";
import { AppShell } from "@/components/app-shell";
import { LoginPage } from "@/features/auth/login-page";
// Dashboard and all report pages are lazy-loaded so recharts is NOT bundled
// into the main entry chunk — it is split into its own async chunk loaded only
// on first dashboard/report visit.
const DashboardPage = lazyRouteComponent(
  () => import("@/features/dashboard/dashboard-page"),
  "DashboardPage",
);
// Phase 08 — report pages (lazy — keep recharts out of main entry)
const DailyReportPage = lazyRouteComponent(
  () => import("@/features/reports/revenue/daily-report-page"),
  "DailyReportPage",
);
const ByCashierReportPage = lazyRouteComponent(
  () => import("@/features/reports/revenue/by-cashier-report-page"),
  "ByCashierReportPage",
);
const ByPaymentMethodReportPage = lazyRouteComponent(
  () => import("@/features/reports/revenue/by-payment-method-report-page"),
  "ByPaymentMethodReportPage",
);
const ByOrderTypeReportPage = lazyRouteComponent(
  () => import("@/features/reports/revenue/by-order-type-report-page"),
  "ByOrderTypeReportPage",
);
const GrossProfitReportPage = lazyRouteComponent(
  () => import("@/features/reports/revenue/gross-profit-report-page"),
  "GrossProfitReportPage",
);
const CashbookReportPage = lazyRouteComponent(
  () => import("@/features/reports/cashbook/cashbook-report-page"),
  "CashbookReportPage",
);
const StockValueReportPage = lazyRouteComponent(
  () => import("@/features/reports/inventory/stock-value-report-page"),
  "StockValueReportPage",
);
const LowStockReportPage = lazyRouteComponent(
  () => import("@/features/reports/inventory/low-stock-report-page"),
  "LowStockReportPage",
);
const SlowMovingReportPage = lazyRouteComponent(
  () => import("@/features/reports/inventory/slow-moving-report-page"),
  "SlowMovingReportPage",
);
const InventoryTransactionsReportPage = lazyRouteComponent(
  () => import("@/features/reports/inventory/inventory-transactions-report-page"),
  "InventoryTransactionsReportPage",
);
const ImportHistoryReportPage = lazyRouteComponent(
  () => import("@/features/reports/inventory/import-history-report-page"),
  "ImportHistoryReportPage",
);
const TopCustomersReportPage = lazyRouteComponent(
  () => import("@/features/reports/customers/top-customers-report-page"),
  "TopCustomersReportPage",
);
const OverdueAgingReportPage = lazyRouteComponent(
  () => import("@/features/reports/customers/overdue-aging-report-page"),
  "OverdueAgingReportPage",
);
const AcquisitionReportPage = lazyRouteComponent(
  () => import("@/features/reports/customers/acquisition-report-page"),
  "AcquisitionReportPage",
);
const CashierReportPage = lazyRouteComponent(
  () => import("@/features/reports/staff/cashier-report-page"),
  "CashierReportPage",
);
const TechnicianReportPage = lazyRouteComponent(
  () => import("@/features/reports/staff/technician-report-page"),
  "TechnicianReportPage",
);
import { ProductsListPage } from "@/features/products/products-list-page";
import { ProductFormPage } from "@/features/products/product-form-page";
import { CategoriesPage } from "@/features/categories/categories-page";
import { UsersPage } from "@/features/admin/users-page";
import { DataImportPage } from "@/features/admin/data-import-page";
import { StockListPage } from "@/features/inventory/stock-list-page";
import { SerialLookupPage } from "@/features/inventory/serial-lookup-page";
import { ImportListPage } from "@/features/inventory/import-list-page";
import { ImportFormPage } from "@/features/inventory/import-form-page";
import { TransferPage } from "@/features/inventory/transfer-page";
import { WarehousesPage } from "@/features/inventory/warehouses-page";
import { SuppliersPage } from "@/features/inventory/suppliers-page";
// Phase 03 — POS, orders, admin settings
import { PosScreenPage } from "@/features/pos/pos-screen-page";
import { OrdersListPage } from "@/features/orders/orders-list-page";
import { OrderDetailPage } from "@/features/orders/order-detail-page";
import { WorkstationsPage } from "@/features/admin/workstations-page";
import { StoreSettingsPage } from "@/features/admin/store-settings-page";
// Phase 06 — warranty
import { PublicLookupPage } from "@/features/warranty/public-lookup-page";
import { ClaimsListPage } from "@/features/warranty/claims-list-page";
import { ClaimDetailPage } from "@/features/warranty/claim-detail-page";
import { ClaimFormPage } from "@/features/warranty/claim-form-page";
// Phase 07 — repair
import { RepairKanbanPage } from "@/features/repair/repair-kanban-page";
import { RepairDetailPage } from "@/features/repair/repair-detail-page";
import { RepairFormPage } from "@/features/repair/repair-form-page";
// Phase 05 — customers, debts, coupons
import { CustomersListPage } from "@/features/customers/customers-list-page";
import { CustomerDetailPage } from "@/features/customers/customer-detail-page";
import { CustomerFormPage } from "@/features/customers/customer-form-page";
import { OverdueListPage } from "@/features/debts/overdue-list-page";
import { DebtOverviewPage } from "@/features/debts/debt-overview-page";
import { CashfundPage } from "@/features/cashfund/cashfund-page";
import { CouponsPage } from "@/features/admin/coupons-page";
// Phase 04 — SePay + Webhook log
import { SepayConfigPage } from "@/features/admin/sepay-config-page";
import { WebhookLogPage } from "@/features/admin/webhook-log-page";
// Phase 09 — audit log, general settings, inventory rebuild
import { AuditLogPage } from "@/features/admin/audit-log-page";
import { GeneralSettingsPage } from "@/features/admin/general-settings-page";
import { InventoryRebuildPage } from "@/features/admin/inventory-rebuild-page";
// Account page — self-service profile + change password
import { AccountPage } from "@/features/account/account-page";
// B2B wholesale sales screen
import { WholesalePage } from "@/features/wholesale/wholesale-page";

interface RouterContext {
  queryClient: QueryClient;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <div className="min-h-screen bg-background text-foreground">
      <Outlet />
    </div>
  ),
});

// ---------------------------------------------------------------------------
// Public routes
// ---------------------------------------------------------------------------
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

// Public warranty lookup — no auth required, sibling to loginRoute.
const publicWarrantyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bao-hanh",
  component: PublicLookupPage,
});

// ---------------------------------------------------------------------------
// Protected layout — wraps all authenticated pages
// ---------------------------------------------------------------------------
const protectedLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: () => (
    <RequireAuth>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Protected child routes — phase 01
// ---------------------------------------------------------------------------
const indexRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/",
  component: DashboardPage, // lazy — recharts chunk loaded on first visit
});

const productsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/products",
  // Optional ?category=<id> — lets the Danh mục page deep-link into a filtered list.
  validateSearch: (search: Record<string, unknown>): { category?: string } => ({
    category: typeof search.category === "string" ? search.category : undefined,
  }),
  component: ProductsListPage,
});

const productsNewRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/products/new",
  component: () => (
    <RequireAuth roles={["admin", "manager", "sales"]}>
      <ProductFormPage />
    </RequireAuth>
  ),
});

const productsIdRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/products/$id",
  component: () => (
    <RequireAuth roles={["admin", "manager", "sales"]}>
      <ProductFormPage />
    </RequireAuth>
  ),
});

const categoriesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/categories",
  component: CategoriesPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/users",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <UsersPage />
    </RequireAuth>
  ),
});

const adminImportRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/import",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <DataImportPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Protected child routes — phase 02 inventory
// ---------------------------------------------------------------------------
const inventoryRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/inventory",
  component: StockListPage,
});

const serialsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/inventory/serials",
  component: SerialLookupPage,
});

const importsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/inventory/imports",
  component: () => (
    <RequireAuth roles={["admin", "manager", "warehouse"]}>
      <ImportListPage />
    </RequireAuth>
  ),
});

const importsNewRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/inventory/imports/new",
  component: () => (
    <RequireAuth roles={["admin", "manager", "warehouse"]}>
      <ImportFormPage />
    </RequireAuth>
  ),
});

const importsIdRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/inventory/imports/$id",
  component: () => (
    <RequireAuth roles={["admin", "manager", "warehouse"]}>
      <ImportFormPage />
    </RequireAuth>
  ),
});

const adminWarehousesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/warehouses",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <WarehousesPage />
    </RequireAuth>
  ),
});

const inventoryTransfersRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/inventory/transfers",
  component: () => (
    <RequireAuth roles={["admin", "manager", "warehouse"]}>
      <TransferPage />
    </RequireAuth>
  ),
});

const adminSuppliersRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/suppliers",
  component: () => (
    <RequireAuth roles={["admin", "manager", "warehouse"]}>
      <SuppliersPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Protected child routes — phase 03 POS + orders + admin
// ---------------------------------------------------------------------------
const posRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/pos",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin"]}>
      <PosScreenPage />
    </RequireAuth>
  ),
});

const ordersRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/orders",
  component: OrdersListPage,
});

const ordersIdRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/orders/$id",
  component: OrderDetailPage,
});

const adminWorkstationsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/workstations",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <WorkstationsPage />
    </RequireAuth>
  ),
});

const adminStoreRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/store",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <StoreSettingsPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Phase 07 — repair routes (protected)
// ---------------------------------------------------------------------------
const repairRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/repair",
  component: () => (
    <RequireAuth roles={["technician", "manager", "admin"]}>
      <RepairKanbanPage />
    </RequireAuth>
  ),
});

const repairNewRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/repair/new",
  component: () => (
    <RequireAuth roles={["technician", "manager", "admin"]}>
      <RepairFormPage />
    </RequireAuth>
  ),
});

const repairIdRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/repair/$id",
  component: () => (
    <RequireAuth roles={["technician", "manager", "admin"]}>
      <RepairDetailPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Phase 06 — warranty routes (protected)
// ---------------------------------------------------------------------------
const warrantyRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/warranty",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin", "technician"]}>
      <ClaimsListPage />
    </RequireAuth>
  ),
});

const warrantyNewRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/warranty/new",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin", "technician"]}>
      <ClaimFormPage />
    </RequireAuth>
  ),
});

const warrantyIdRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/warranty/$id",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin", "technician"]}>
      <ClaimDetailPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Phase 05 — customer routes
// ---------------------------------------------------------------------------
const customersRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/customers",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin"]}>
      <CustomersListPage />
    </RequireAuth>
  ),
});

const customersNewRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/customers/new",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin"]}>
      <CustomerFormPage />
    </RequireAuth>
  ),
});

const customersIdRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/customers/$id",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin"]}>
      <CustomerDetailPage />
    </RequireAuth>
  ),
});

const customersIdEditRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/customers/$id/edit",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin"]}>
      <CustomerFormPage />
    </RequireAuth>
  ),
});

const debtsOverdueRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/debts/overdue",
  component: () => (
    <RequireAuth roles={["manager", "admin", "sales"]}>
      <OverdueListPage />
    </RequireAuth>
  ),
});

const debtsOverviewRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/debts",
  component: () => (
    <RequireAuth roles={["manager", "admin", "sales"]}>
      <DebtOverviewPage />
    </RequireAuth>
  ),
});

const cashfundRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/cashfund",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <CashfundPage />
    </RequireAuth>
  ),
});

const adminCouponsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/coupons",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <CouponsPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Phase 04 — SePay config + webhook log
// ---------------------------------------------------------------------------
const adminSepayRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/sepay",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <SepayConfigPage />
    </RequireAuth>
  ),
});

const adminWebhooksRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/webhooks",
  component: () => (
    <RequireAuth roles={["admin", "manager"]}>
      <WebhookLogPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Phase 08 — report routes (lazy components — recharts loads async)
// ---------------------------------------------------------------------------
const reportRevenueDailyRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/revenue/daily",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <DailyReportPage />
    </RequireAuth>
  ),
});

const reportRevenueByCashierRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/revenue/by-cashier",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <ByCashierReportPage />
    </RequireAuth>
  ),
});

const reportRevenueByPaymentRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/revenue/by-payment-method",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <ByPaymentMethodReportPage />
    </RequireAuth>
  ),
});

const reportRevenueByOrderTypeRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/revenue/by-order-type",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <ByOrderTypeReportPage />
    </RequireAuth>
  ),
});

const reportGrossProfitRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/revenue/gross-profit",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <GrossProfitReportPage />
    </RequireAuth>
  ),
});

const reportCashbookRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/cashbook",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <CashbookReportPage />
    </RequireAuth>
  ),
});

const reportStockValueRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/inventory/stock-value",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <StockValueReportPage />
    </RequireAuth>
  ),
});

const reportLowStockRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/inventory/low-stock",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <LowStockReportPage />
    </RequireAuth>
  ),
});

const reportSlowMovingRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/inventory/slow-moving",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <SlowMovingReportPage />
    </RequireAuth>
  ),
});

const reportInvTransactionsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/inventory/transactions",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <InventoryTransactionsReportPage />
    </RequireAuth>
  ),
});

const reportImportHistoryRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/inventory/import-history",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <ImportHistoryReportPage />
    </RequireAuth>
  ),
});

const reportTopCustomersRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/customers/top",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <TopCustomersReportPage />
    </RequireAuth>
  ),
});

const reportOverdueAgingRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/customers/overdue-aging",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <OverdueAgingReportPage />
    </RequireAuth>
  ),
});

const reportAcquisitionRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/customers/acquisition",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <AcquisitionReportPage />
    </RequireAuth>
  ),
});

const reportStaffCashierRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/staff/cashier",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin"]}>
      <CashierReportPage />
    </RequireAuth>
  ),
});

const reportStaffTechnicianRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/reports/staff/technician",
  component: () => (
    <RequireAuth roles={["manager", "admin"]}>
      <TechnicianReportPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Phase 09 — audit log, general settings, inventory rebuild
// ---------------------------------------------------------------------------
const adminAuditLogRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/audit-log",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <AuditLogPage />
    </RequireAuth>
  ),
});

const adminGeneralSettingsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/general",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <GeneralSettingsPage />
    </RequireAuth>
  ),
});

const adminInventoryRebuildRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/admin/inventory/rebuild",
  component: () => (
    <RequireAuth roles={["admin"]}>
      <InventoryRebuildPage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Account — self-service profile + change password (all authenticated users)
// ---------------------------------------------------------------------------
const accountRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/account",
  component: AccountPage,
});

// ---------------------------------------------------------------------------
// B2B wholesale sales screen
// ---------------------------------------------------------------------------
const wholesaleRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: "/wholesale",
  component: () => (
    <RequireAuth roles={["cashier", "manager", "admin"]}>
      <WholesalePage />
    </RequireAuth>
  ),
});

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------
export const routeTree = rootRoute.addChildren([
  loginRoute,
  publicWarrantyRoute,
  protectedLayout.addChildren([
    indexRoute,
    productsRoute,
    productsNewRoute,
    productsIdRoute,
    categoriesRoute,
    adminUsersRoute,
    adminImportRoute,
    // Phase 02 — inventory routes (more specific paths first)
    importsNewRoute,
    importsIdRoute,
    importsRoute,
    inventoryTransfersRoute,
    serialsRoute,
    inventoryRoute,
    adminWarehousesRoute,
    adminSuppliersRoute,
    // Phase 03 — POS, orders, admin
    posRoute,
    ordersIdRoute,
    ordersRoute,
    adminWorkstationsRoute,
    adminStoreRoute,
    // Phase 07 — repair (more specific paths first)
    repairNewRoute,
    repairIdRoute,
    repairRoute,
    // Phase 06 — warranty (more specific paths first)
    warrantyNewRoute,
    warrantyIdRoute,
    warrantyRoute,
    // Phase 05 — customers, debts, coupons (more specific paths first)
    customersNewRoute,
    customersIdEditRoute,
    customersIdRoute,
    customersRoute,
    debtsOverdueRoute,
    debtsOverviewRoute,
    cashfundRoute,
    adminCouponsRoute,
    // Phase 04 — SePay + webhook log
    adminSepayRoute,
    adminWebhooksRoute,
    // Phase 09 — audit log, general settings, inventory rebuild
    adminAuditLogRoute,
    adminGeneralSettingsRoute,
    adminInventoryRebuildRoute,
    // Account
    accountRoute,
    // B2B wholesale
    wholesaleRoute,
    // Phase 08 — reports
    reportRevenueDailyRoute,
    reportRevenueByCashierRoute,
    reportRevenueByPaymentRoute,
    reportRevenueByOrderTypeRoute,
    reportGrossProfitRoute,
    reportCashbookRoute,
    reportStockValueRoute,
    reportLowStockRoute,
    reportSlowMovingRoute,
    reportInvTransactionsRoute,
    reportImportHistoryRoute,
    reportTopCustomersRoute,
    reportOverdueAgingRoute,
    reportAcquisitionRoute,
    reportStaffCashierRoute,
    reportStaffTechnicianRoute,
  ]),
]);
